import React, { useCallback, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import moment from 'moment';
import { isStaff } from '../../../helpers/global';
import { USA_STATES } from './../../../helpers/constants';
import Select from 'react-select';
import CustomCreatableSelect from '../../common/CreatableSelect';
import useCountyCityOptions from '../../../hooks/useCountyCityOptions';
import useAppChoices from '../../../hooks/useAppChoices';
import AttachSupportingDocuments from './AttachSupportingDocuments';
import RequestedByField, { useRequestedByField } from '../../common/RequestedByField';

export const ServiceForm = ({
  defaultValues,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  orgs,
  role,
  onOrganizationChange
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const users = useAppChoices('users');
  const organizations = orgs
    .map(org => ({ value: org._id, label: org.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const selectedOrganization = organizations.find(
    org => org.value === (defaultValues?.organization ?? formData.organization)
  );
  const { cities } = useCountyCityOptions();

  const [organization, setOrganization] = useState(selectedOrganization);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...defaultValues }));
  }, [defaultValues]);

  useEffect(() => {
    if (!formData?.requestedBy) return;
    setFormErrors(prev => (prev.requestedBy ? { ...prev, requestedBy: '' } : prev));
  }, [formData?.requestedBy, setFormErrors]);

  const handleChange = e => {
    if (!e?.target) return;

    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const newData = { ...prev };

      if (name.includes('individual')) {
        const [field, fieldName] = name.split('.');
        newData[field] = { ...prev[field], [fieldName]: value };
      } else if (type === 'radio') {
        newData[name] = checked ? value : prev[name];
      } else {
        newData[name] = value;
      }
      return newData;
    });

    // restrict fileName length to 250 chars in real-time
    if (name === 'fileName' && value.length > 250) {
      setFormErrors(prev => ({ ...prev, [name]: 'File name cannot exceed 250 characters.' }));
    } else {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const onToggleIsIndividualService = value => {
    setFormData(prev => ({ ...prev, isIndividualService: value }));
  };

  const setRequestedBy = useCallback(id => {
    setFormData(prev => {
      if ((prev.requestedBy || '') === (id || '')) return prev;
      return { ...prev, requestedBy: id || '' };
    });
    setFormErrors(prev => (prev.requestedBy ? { ...prev, requestedBy: '' } : prev));
  }, [setFormData, setFormErrors]);

  const { applies: showRequestedBy, eligible: requestedByUsers } = useRequestedByField({
    users,
    organizations: orgs,
    organizationId: formData.organization,
    isIndividual: formData?.isIndividualService,
    enabled: isStaff(role),
    setFormData
  });

  const handleOrganizationSelect = e => {
    setOrganization(e);
    setFormData(prev => ({
      ...prev,
      organization: e?.value || '',
      requestedBy: ''
    }));
    setFormErrors(prev => ({ ...prev, organization: '', requestedBy: '' }));
    onOrganizationChange(e?.value);
  };

  const handleSupportingDocumentsChange = documentsOrUpdater => {
    setFormData(prev => ({
      ...prev,
      supportingDocuments:
        typeof documentsOrUpdater === 'function'
          ? documentsOrUpdater(prev.supportingDocuments || [])
          : documentsOrUpdater
    }));
  };

  const handleDatePaste = e => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();
    
    // Try to parse with moment - it handles MM/DD/YYYY, M/D/YYYY, etc.
    const date = moment(pastedText, ['MM/DD/YYYY', 'M/D/YYYY', 'MM-DD-YYYY', 'M-D-YYYY'], true);
    
    if (date.isValid()) {
      const formattedDate = date.format('YYYY-MM-DD');
      setFormData(prev => ({ ...prev, closingDate: formattedDate }));
      setFormErrors(prev => ({ ...prev, closingDate: '' }));
    } else {
      // Clear field and show error for invalid format
      setFormData(prev => ({ ...prev, closingDate: '' }));
      setFormErrors(prev => ({ ...prev, closingDate: 'Invalid date format' }));
    }
  };

  const cityOptions = cities.map(city => ({ label: city, value: city }));
  const stateOptions = USA_STATES.map(state => ({ ...state, label: state.name, value: state.name }));

  return (
    <>
      <Row>
        <Col md={12}>
          <p className="col-title my-2">File Details</p>
        </Col>
        <Col>
          <Row>
            <Col md={3} xs={12} className="align-self-end">
              <Form.Group className="midFont">
                <Form.Label htmlFor="fileName" className="font-weight-medium">
                  File Name/Number<span className="text-danger">*</span>
                </Form.Label>
                <input
                  placeholder="File Name"
                  required
                  id="fileName"
                  name="fileName"
                  value={formData?.fileName}
                  onChange={handleChange}
                  type="text"
                  className={`form-control form-control-sm ${formErrors?.fileName ? 'is-invalid' : ''}`}
                />
                {formErrors?.fileName && <p className="xsmallFont text-rush">{formErrors?.fileName}</p>}
              </Form.Group>
            </Col>
            <Col className="align-self-end" md={3} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="folioNumbers" className="font-weight-medium">
                  Folio <span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  placeholder="Folio Number"
                  id="folioNumbers"
                  name="folioNumbers"
                  value={formData?.folioNumbers}
                  onChange={handleChange}
                  type="text"
                  className={`form-control form-control-sm ${formErrors?.folioNumbers ? 'is-invalid' : ''}`}
                />
                {formErrors?.folioNumbers && <p className="xsmallFont text-rush">{formErrors?.folioNumbers}</p>}
              </Form.Group>
            </Col>
            <Col md={2} xs={12}>
              <Form.Group htmlFor="closingDate" className="font-weight-medium">
                <Form.Label>
                  Closing Date<span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  type="date"
                  id="closingDate"
                  name="closingDate"
                  value={formData?.closingDate}
                  onChange={handleChange}
                  onPaste={handleDatePaste}
                  className={`form-control form-control-sm ${formErrors?.closingDate ? 'is-invalid' : ''}`}
                />
                {formErrors?.closingDate && <p className="xsmallFont text-rush">{formErrors?.closingDate}</p>}
              </Form.Group>
            </Col>
            <Col md={2} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="buyerName" className="font-weight-medium">
                  Buyer Name<span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  placeholder="Buyer Name"
                  type="text"
                  className={`form-control form-control-sm ${formErrors?.buyerName ? 'is-invalid' : ''}`}
                  id="buyerName"
                  name="buyerName"
                  value={formData?.buyerName}
                  onChange={handleChange}
                />
                {formErrors?.buyerName && <p className="xsmallFont text-rush">{formErrors?.buyerName}</p>}
              </Form.Group>
            </Col>
            <Col md={2} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="sellerName" className="font-weight-medium">
                  Seller Name<span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  id="sellerName"
                  name="sellerName"
                  value={formData?.sellerName}
                  onChange={handleChange}
                  placeholder="Seller Name"
                  type="text"
                  className={`form-control form-control-sm ${formErrors?.sellerName ? 'is-invalid' : ''}`}
                />
                {formErrors?.sellerName && <p className="xsmallFont text-rush">{formErrors?.sellerName}</p>}
              </Form.Group>
            </Col>
          </Row>
          {isStaff(role) && (
            <Row className="align-items-end mt-2">
              {formData?.isIndividualService ? (
                <>
                  <Col md={3} xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="individualName">
                        <div className="d-flex align-items-center ">
                          <span>
                            Individual Name<span className="text-danger">*</span>
                          </span>

                          <div className="ml-4 toggle-link" onClick={() => onToggleIsIndividualService(false)}>
                            Is Organization?
                          </div>
                        </div>
                      </Form.Label>
                      <input
                        placeholder="Individual Name"
                        required
                        id="individual.name"
                        name="individual.name"
                        value={formData?.individual?.name || ''}
                        onChange={handleChange}
                        type="text"
                        className={`form-control form-control-sm ${formErrors?.individual?.name ? 'is-invalid' : ''}`}
                      />
                      {formErrors?.individual?.name && (
                        <p className="xsmallFont text-rush">{formErrors?.individual?.name}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3} xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="individualEmail">
                        Individual Email<span className="text-danger">*</span>
                      </Form.Label>
                      <input
                        placeholder="Email"
                        required
                        id="individual.email"
                        name="individual.email"
                        value={formData?.individual?.email || ''}
                        onChange={handleChange}
                        type="email"
                        className={`form-control form-control-sm ${formErrors?.individual?.email ? 'is-invalid' : ''}`}
                      />
                      {formErrors?.individual?.email && (
                        <p className="xsmallFont text-rush">{formErrors?.individual?.email}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3} xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="individualPhoneNumber">Individual Phone Number</Form.Label>
                      <input
                        placeholder="Phone Number"
                        id="individual.phoneNumber"
                        name="individual.phoneNumber"
                        value={formData?.individual?.phoneNumber || ''}
                        onChange={handleChange}
                        type="tel"
                        className="form-control form-control-sm"
                      />
                    </Form.Group>
                  </Col>
                </>
              ) : (
                <Col md={3} xs={12}>
                  <Form.Group>
                    <Form.Label>
                      <div className="d-flex align-items-center ">
                        <span>
                          Organization<span className="text-danger">*</span>
                        </span>

                        <div className="ml-4 toggle-link" onClick={() => onToggleIsIndividualService(true)}>
                          Is Individual?
                        </div>
                      </div>
                    </Form.Label>
                    <Select
                      id="organization"
                      name="organization"
                      classNamePrefix="select"
                      required
                      menuIsOpen={isMenuOpen}
                      isSearchable
                      onMenuOpen={() => setIsMenuOpen(true)}
                      onMenuClose={() => setIsMenuOpen(false)}
                      onChange={handleOrganizationSelect}
                      isClearable
                      className={`select-organization ${formErrors?.organization ? 'is-invalid' : ''}`}
                      value={organization}
                      options={organizations}
                    />
                    {formErrors.organization && <p className="xsmallFont text-rush">This is a required Field</p>}
                  </Form.Group>
                </Col>
              )}
              {showRequestedBy && (
                <Col md={4} xs={12}>
                  <RequestedByField
                    eligible={requestedByUsers}
                    value={formData?.requestedBy || ''}
                    onChange={setRequestedBy}
                    error={formErrors?.requestedBy}
                  />
                </Col>
              )}
            </Row>
          )}
        </Col>
      </Row>

      {/* Second Row */}
      <Row>
        <Col md={12}>
          <hr className="my-1" />
          <p className="col-title my-2">Address Details</p>
        </Col>
        <Col>
          <Row>
            <Col md={3} xs={12} className="align-self-end">
              <Form.Group className="midFont">
                <Form.Label htmlFor="propertyAddress" className="font-weight-medium">
                  Property Address<span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  placeholder="Property Address"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData?.propertyAddress}
                  onChange={handleChange}
                  className={`form-control form-control-sm ${formErrors?.propertyAddress ? 'is-invalid' : ''}`}
                />
                {formErrors?.propertyAddress && <p className="xsmallFont text-rush">{formErrors?.propertyAddress}</p>}
              </Form.Group>
            </Col>
            <Col md={2} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="city" className="font-weight-medium">
                  City<span className="text-danger">*</span>
                </Form.Label>
                <CustomCreatableSelect
                  id="city"
                  name="city"
                  required
                  placeholder="City"
                  options={cityOptions}
                  value={formData.city ? { label: formData.city, value: formData.city } : ''}
                  classNamePrefix="select"
                  className="basic-single select-organization"
                  onChange={e =>
                    setFormData({
                      ...formData,
                      city: e?.value || ''
                    })
                  }
                  menuPlacement="auto"
                />
                {formErrors?.city && <p className="xsmallFont text-rush">{formErrors?.city}</p>}
              </Form.Group>
            </Col>
            <Col md={3} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="state" className="font-weight-medium">
                  State<span className="text-danger">*</span>
                </Form.Label>
                <Select
                  id="state"
                  name="state"
                  required
                  placeholder="State"
                  options={stateOptions}
                  classNamePrefix="select"
                  className="basic-single select-organization"
                  value={formData.state ? { label: formData.state, value: formData.state } : stateOptions[0]}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      state: e?.value || ''
                    })
                  }
                  menuPlacement="auto"
                />
                {formErrors?.state && <p className="xsmallFont text-rush">{formErrors?.state}</p>}
              </Form.Group>
            </Col>
            <Col md={2} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="zipCode" className="font-weight-medium">
                  Zip Code<span className="text-danger">*</span>
                </Form.Label>
                <input
                  required
                  placeholder="Zip Code"
                  id="zipCode"
                  type="text"
                  className={`form-control form-control-sm ${formErrors?.zipCode ? 'is-invalid' : ''}`}
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
                {formErrors?.zipCode && <p className="xsmallFont text-rush">{formErrors?.zipCode}</p>}
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* other details */}
      <Row>
        <Col md={12}>
          <hr className="my-1" />
          <p className="col-title my-2">Other Details</p>
        </Col>
        <Col xs={12}>
          <Row>
            <Col md={4} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor={'modificationAndSpecialInstructions'} className="font-weight-medium">
                  Modifications / Special Instruction
                </Form.Label>
                <textarea
                  name="modificationAndSpecialInstructions"
                  id="modificationAndSpecialInstructions"
                  value={formData?.modificationAndSpecialInstructions}
                  onChange={handleChange}
                  placeholder="Some modification specifications"
                  className="form-control form-control-sm mt-1"
                  rows="3"
                ></textarea>
              </Form.Group>
            </Col>
            <Col md={4} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="legalDescription" className="font-weight-medium">
                  Legal Description
                </Form.Label>
                <textarea
                  name="legalDescription"
                  value={formData?.legalDescription}
                  onChange={handleChange}
                  placeholder="Legal description"
                  id="legalDescription"
                  className="form-control form-control-sm mt-1"
                  rows="3"
                ></textarea>
              </Form.Group>
            </Col>
            <Col md={4} xs={12}>
              <Form.Group className="midFont">
                <Form.Label htmlFor="supportingDocuments" className="font-weight-medium">
                  <span>Attach Supporting Documents</span> <span style={{ padding: '1px 4px' }} className='bg-success text-white rounded ml-2 fw-bold tinyFont'>NEW</span>
                </Form.Label>
                <AttachSupportingDocuments
                  documents={formData?.supportingDocuments}
                  onDocumentsChange={handleSupportingDocumentsChange}
                />
                {formErrors?.supportingDocuments && (
                  <p className="xsmallFont text-rush">{formErrors?.supportingDocuments}</p>
                )}
              </Form.Group>
            </Col>
          </Row>

        </Col>
      </Row>
    </>
  );
};
